// Piezas compartidas de las pestañas de la app WhatsApp que tienen secciones
// (Difusión, Configuración): menú lateral en escritorio / fila deslizable en
// el celular, contenido con su propio scroll y el aviso de marca al pie.
// La sección activa vive en la URL (?s=…) para que los enlaces viejos
// (/m/whatsapp/templates → /m/whatsapp-app/difusion?s=plantillas) y los
// marcadores caigan justo donde deben.

import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';

export interface Section<K extends string> {
  key: K;
  label: string;
  /** Etiqueta corta para el celular (si la larga no cabe). */
  short?: string;
  Icon: LucideIcon;
}

/** Sección activa desde `?s=`; si no viene o no existe, la primera. */
export function useSection<K extends string>(sections: Section<K>[]): [K, (k: K) => void] {
  const [sp, setSp] = useSearchParams();
  const raw = sp.get('s');
  const current = (sections.find((s) => s.key === raw)?.key ?? sections[0].key) as K;
  const set = (k: K) => {
    const next = new URLSearchParams(sp);
    next.set('s', k);
    setSp(next, { replace: true });
  };
  return [current, set];
}

export function SectionLayout<K extends string>({ label, sections, current, onSelect, isMobile, children, wide = false }: {
  label: string;
  sections: Section<K>[];
  current: K;
  onSelect: (k: K) => void;
  isMobile: boolean;
  children: ReactNode;
  /** Tablas (contactos) aprovechan el ancho; formularios se quedan en 3xl. */
  wide?: boolean;
}) {
  // En el celular, con pocas secciones (Difusión: 3) caben todas a la vista en
  // una rejilla; con más (Configuración: 5) es una fila que se desliza.
  const grid = isMobile && sections.length <= 3;
  return (
    <div className={`flex h-full min-h-0 ${isMobile ? 'flex-col' : ''}`}>
      <nav aria-label={label}
        className={isMobile ? `border-b border-border bg-card px-3 py-2 ${grid ? '' : 'no-scrollbar overflow-x-auto'}` : 'w-56 shrink-0 border-r border-border bg-card p-3'}>
        <ul className={grid ? 'grid gap-1' : isMobile ? 'flex gap-1' : 'space-y-0.5'}
          style={grid ? { gridTemplateColumns: `repeat(${sections.length}, minmax(0, 1fr))` } : undefined}>
          {sections.map(({ key, label: l, short, Icon }) => (
            <li key={key} className="min-w-0">
              <button type="button" onClick={() => onSelect(key)} aria-current={current === key ? 'page' : undefined}
                className={[
                  grid
                    ? 'flex w-full items-center justify-center gap-1.5 rounded-lg px-2 py-2 text-[12.5px] transition-colors'
                    : 'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-[13.5px] whitespace-nowrap transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))]',
                  current === key ? 'bg-muted font-semibold text-foreground' : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                ].join(' ')}>
                <Icon className="h-4 w-4 shrink-0" aria-hidden /> <span className={grid ? 'truncate' : ''}>{grid && short ? short : l}</span>
              </button>
            </li>
          ))}
        </ul>
      </nav>
      <div className="wa-scroll flex-1 min-h-0 overflow-y-auto bg-background p-4 sm:p-6">
        <div className={`mx-auto ${wide ? 'max-w-5xl' : 'max-w-3xl'}`}>
          {children}
          <BrandNote />
        </div>
      </div>
    </div>
  );
}

/** Contenedor con scroll para pestañas sin secciones (Números y conexión). */
export function ScrollPane({ children }: { children: ReactNode }) {
  return (
    <div className="wa-scroll h-full min-h-0 overflow-y-auto bg-background p-4 sm:p-6">
      <div className="mx-auto max-w-3xl">
        {children}
        <BrandNote />
      </div>
    </div>
  );
}

export function BrandNote() {
  return (
    <p className="mt-10 border-t border-border pt-3 text-[11.5px] text-muted-foreground">
      WhatsApp es una marca de WhatsApp LLC. Esta app es parte de Orion y se conecta a tu número por la API oficial
      (WhatsApp Cloud API de Meta o Twilio); no es un producto de Meta ni está respaldada por Meta.
    </p>
  );
}

export function H({ title, sub, action }: { title: string; sub?: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-4 flex flex-wrap items-start gap-3">
      <div className="min-w-0 flex-1">
        <h2 className="text-[18px] font-semibold text-foreground">{title}</h2>
        {sub && <p className="mt-1 text-[13.5px] text-muted-foreground">{sub}</p>}
      </div>
      {action}
    </div>
  );
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`rounded-2xl border border-border bg-card p-4 text-card-foreground ${className}`}>{children}</div>;
}
